import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SinglePurchaseData, TableColumn } from '../../../../types'
import { useParams } from 'react-router-dom'
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
  Typography,
} from '@mui/material'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import PurchaseHistoryTimeline from '../PurchaseHistoryTimeline'
import { History as HistoryIcon, Close as CloseIcon } from '@mui/icons-material'
import { calculateInvoiceTotalPaid } from '../../../../../../utils/calculateInvoiceTotalPaid'
import { calculateInvoiceBalanceAmount } from '../../../../../../utils/calculateInvoiceBalanceAmount'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowPurchaseInvoice = () => {
  const [loading, setLoading] = useState(true);
  const [singleInvoice, setSingleInvoice] = useState<SinglePurchaseData | null>(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();

  const params = useParams()
  const purchaseId = params.purchaseId

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/businessInfo",
        method: "get",
      });

      if (res.success) {
        setBusinessInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch business info");
    }
  }, []);
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/taxInfo",
        method: "get",
      });

      if (res.success) {
        setTaxInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch tax info");
    }
  }, []);
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])

  const fetchSingleInvoice = useCallback(async () => {
    if (!purchaseId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `purchase/get-single-purchase-invoice/${purchaseId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSingleInvoice(res.data)
      } else {
        toast.error(res.message || "Failed to fetch invoice");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching invoice");
    } finally {
      setLoading(false)
    }
  }, [purchaseId])

  useEffect(() => {
    fetchSingleInvoice();
  }, [fetchSingleInvoice])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('purchase_invoice', {
          title: `Purchase Invoice - ${singleInvoice?.purchaseNumber}'}`,
          fileName: `Purchase _${singleInvoice?.purchaseNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singleInvoice?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singleInvoice.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  // Prepare table data from invoice items
  const prepareTableData = () => {
    if (!singleInvoice?.items?.length) return [];

    return singleInvoice.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singleInvoice?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singleInvoice) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singleInvoice?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST invoice
    if (singleInvoice.gstType === 'GST' && singleInvoice.taxBreakdown?.length > 0) {
      singleInvoice.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singleInvoice.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singleInvoice.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singleInvoice.charges?.length > 0) {
      singleInvoice.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singleInvoice.discount?.amount && singleInvoice.discount.amount > 0) {
      const discountLabel = singleInvoice.discount.isPercentage
        ? `Discount (${singleInvoice.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singleInvoice.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singleInvoice.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singleInvoice.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singleInvoice) return [];

    const fields = [];

    if (singleInvoice.receivedAmount >= 0) {
      fields.push({
        label: "Paid Amount",
        value: calculateInvoiceTotalPaid(singleInvoice?.paymentReferences, singleInvoice?.receivedAmount)
      });
    }

    if (singleInvoice.balanceAmount >= 0) {
      fields.push({
        label: "Balance Due",
        // value: singleInvoice.balanceAmount
        value: calculateInvoiceBalanceAmount(singleInvoice.totalAmount, singleInvoice?.paymentReferences, singleInvoice?.receivedAmount)
      });
    }

    return fields;
  };

  const toggleHistoryDrawer = () => {
    setHistoryDrawerOpen(!historyDrawerOpen);
  };

  if (!singleInvoice && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Sales Invoice'
          subtitle='Invoice not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Purchase Invoice'
        subtitle={`#${singleInvoice?.purchaseNumber || 'Loading...'}`}
        status={singleInvoice?.status || 'UNPAID'}
        statusColor={singleInvoice?.status === 'PAID' ? 'success' :
          singleInvoice?.status === 'PARTIAL_PAID' ? 'info' : singleInvoice?.status === 'UNPAID' ? 'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print Purchase Invoice'
          pdfLabel='Export PDF'
          disabled={loading || !singleInvoice}
          showPDF={false}
        />

        {/* History Button */}
        <Tooltip title="View Purchase Invoice History" arrow placement='top'>
          <IconButton
            onClick={toggleHistoryDrawer}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              width: 30,
              height: 30,
            }}
          >
            <Badge
              color="secondary"
              variant="dot"
              invisible={false}
              sx={{
                '& .MuiBadge-badge': {
                  right: 2,
                  top: 2,
                }
              }}
            >
              <HistoryIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* <CommonCreateButton
          label="Record Payment"
          onClick={() => alert('Record Payment clicked')}
          disabled={singleInvoice?.status === 'PAID'}
        /> */}
      </Box>

      <PrintableContainer id="purchase_invoice">
        <CommonInvoiceTemplate
          documentType={singleInvoice?.purchaseType || "PURCHASE"}
          title={singleInvoice?.gstType === 'GST' ? "PURCHASE" : "PURCHASE"}

          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singleInvoice?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : ""}

          documentNumber={singleInvoice?.purchaseNumber || ''}
          documentDate={formatDate(singleInvoice?.purchaseDate || singleInvoice?.date || '')}
          dueDate={formatDate(singleInvoice?.dueDate || '')}
          partyHeading='Bill From'
          partyName={singleInvoice?.party?.partyName || singleInvoice?.vendor?.vendorName || 'N/A'}
          partyLocation={singleInvoice?.party?.location || singleInvoice?.vendor?.location}
          partyAddress={
            (singleInvoice?.party?.address) ? (`${singleInvoice.party.address.line1} ${singleInvoice.party.address.city} ${singleInvoice.party.address.state}  ${singleInvoice.party.address.pinCode}`) : (singleInvoice?.vendor?.address) ? (`${singleInvoice.vendor.address.line1} ${singleInvoice.vendor.address.city} ${singleInvoice.vendor.address.state}  ${singleInvoice.vendor.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singleInvoice?.party?.contact?.phone?.[0]?.phoneNo || singleInvoice?.vendor?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singleInvoice?.party?.gstNumber || singleInvoice?.vendor?.gstNumber || 'N/A'}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singleInvoice?.totalAmount,
            taxableAmount: singleInvoice?.taxableAmount,
            subtotal: singleInvoice?.subtotal,
            receivedAmount: calculateInvoiceTotalPaid(singleInvoice?.paymentReferences, singleInvoice?.receivedAmount),
            balanceAmount: singleInvoice?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Purchase Invoice..."
          // notes={singleInvoice?.notes || ''}
          termsConditions={singleInvoice?.terms || ''}
          gstType={singleInvoice?.gstType}
          invoiceStatus={singleInvoice?.status}
        />
      </PrintableContainer>

      {/* History Drawer */}
      <Drawer
        anchor="right"
        open={historyDrawerOpen}
        onClose={toggleHistoryDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 500,
            maxWidth: '100%',
            p: 2,
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Purchase Invoice History
            </Typography>
          </Box>
          <IconButton onClick={toggleHistoryDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {purchaseId && singleInvoice?.gstType && (
          <PurchaseHistoryTimeline
            purchaseId={purchaseId}
            gstType={singleInvoice.gstType}
          />
        )}
      </Drawer>
    </>
  )
}

export default ShowPurchaseInvoice